<template>
  <div class="ad-strategy-config">
    <FormItem label="广告策略：" :label-width="120">
      <Select
        v-model="selectedTemplateId"
        clearable
        filterable
        placeholder="请选择已有广告策略"
        :disabled="disabled"
        :loading="templateLoading"
        @on-change="handleTemplateChange"
      >
        <Option
          v-for="item in templateOptions"
          :key="item.id"
          :value="item.id"
          :label="item.name || item.strategyName"
        >
          {{ item.name || item.strategyName }}
        </Option>
      </Select>
    </FormItem>

    <div class="group-list">
      <div class="group-card">
        <h3 class="group-title">对照组</h3>
        <AdStrategyFields
          v-if="controlGroup"
          group-key="control"
          :config="controlGroup"
          :disabled="disabled"
        />
      </div>

      <div
        v-for="(group, index) in formData.groups"
        :key="index"
        class="group-card"
      >
        <h3 class="group-title">实验组{{ index + 1 }}</h3>
        <AdStrategyFields
          :group-key="'experiment-' + index"
          :config="group.experimentGroupParam"
          :disabled="disabled"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { advertisePage, getAdStrategyInfo } from '@/api/advertise'
import {
  buildAdStrategyTemplatePayload,
  normalizeGroupParamByCapability
} from '../helpers'
import AdStrategyFields from './AdStrategyFields.vue'

const clone = (value) => JSON.parse(JSON.stringify(value))

export default {
  name: 'AdStrategyConfig',
  components: {
    AdStrategyFields
  },
  props: {
    formData: {
      type: Object,
      required: true
    },
    experimentGroupCount: {
      type: Number,
      default: 1
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedTemplateId: null,
      templateOptions: [],
      templateLoading: false
    }
  },
  computed: {
    controlGroup() {
      return this.formData.controlGroupParam || null
    },
    selectedCrowdIds() {
      return Array.isArray(this.formData.crowdIds) ? this.formData.crowdIds : []
    }
  },
  watch: {
    selectedCrowdIds: {
      deep: true,
      handler() {
        this.fetchTemplates()
      }
    }
  },
  created() {
    this.fetchTemplates()
  },
  methods: {
    buildTemplateQueryParams() {
      return {
        current: 1,
        size: 20000
      }
    },
    async fetchTemplates() {
      this.templateLoading = true
      try {
        const { data } = await advertisePage(this.buildTemplateQueryParams())
        this.templateOptions = data.records || []
      } catch (error) {
        this.$Message.error('获取广告策略模板失败')
      } finally {
        this.templateLoading = false
      }
    },
    applyTemplateToForms(templateConfig) {
      this.formData.controlGroupParam = clone(templateConfig)
      this.formData.groups.forEach((group) => {
        group.experimentGroupParam = clone(templateConfig)
      })
    },
    async handleTemplateChange(id) {
      if (!id) return
      try {
        const { data } = await getAdStrategyInfo(id)
        const templateConfig = buildAdStrategyTemplatePayload(data)
        this.applyTemplateToForms(templateConfig)
      } catch (error) {
        this.$Message.error('获取广告策略模板详情失败')
      }
    },
    validatePositiveNumber(value) {
      return value !== null && value !== undefined && Number(value) > 0
    },
    validateRewardRules(config) {
      return (
        Array.isArray(config.signInAd.rewardRules) &&
        config.signInAd.rewardRules.length > 0 &&
        !config.signInAd.rewardRules.some(
          (item) =>
            item.coinAmount === null ||
            item.coinAmount === undefined ||
            item.coinAmount === ''
        )
      )
    },
    validate() {
      const groups = [
        { experimentGroupParam: this.formData.controlGroupParam },
        ...(this.formData.groups || [])
      ]

      return groups.every((group) => {
        const config = normalizeGroupParamByCapability(
          '3',
          group.experimentGroupParam
        )

        if (config.signInAd.enabled) {
          if (
            !this.validatePositiveNumber(config.signInAd.dailyLimit) ||
            !this.validatePositiveNumber(config.signInAd.adsPerReward)
          ) {
            this.$Message.error('请完善签到位广告配置')
            return false
          }

          if (!this.validatePositiveNumber(config.signInAd.h5StaySeconds)) {
            this.$Message.error('请输入有效的签到位广告停留时长')
            return false
          }

          if (!this.validateRewardRules(config)) {
            this.$Message.error('请完善签到位广告奖励赠币规则')
            return false
          }
        }

        if (config.unlockAd.enabled) {
          if (
            !this.validatePositiveNumber(config.unlockAd.adsPerUnlock) ||
            !this.validatePositiveNumber(config.unlockAd.episodesPerUnlock) ||
            !this.validatePositiveNumber(config.unlockAd.dailyUnlockLimit)
          ) {
            this.$Message.error('请完善剧集解锁广告配置')
            return false
          }

          if (!this.validatePositiveNumber(config.unlockAd.h5StaySeconds)) {
            this.$Message.error('请输入有效的剧集解锁广告停留时长')
            return false
          }
        }

        if (
          config.splashAd.enabled &&
          (!config.splashAd.triggerTypes ||
            !config.splashAd.triggerTypes.length)
        ) {
          this.$Message.error('请选择开屏广告类型')
          return false
        }

        if (
          config.feedAd.enabled &&
          (!config.feedAd.positions || !config.feedAd.positions.length)
        ) {
          this.$Message.error('请选择信息流广告位置')
          return false
        }

        if (
          config.interstitialAd.enabled &&
          (!config.interstitialAd.triggerScenes ||
            !config.interstitialAd.triggerScenes.length)
        ) {
          this.$Message.error('请至少选择一个插屏广告触发场景')
          return false
        }

        return true
      })
    }
  }
}
</script>

<style scoped lang="less">
.ad-strategy-config {
  margin: 20px 0;
}

.group-list {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.group-card {
  flex: 0 1 calc(50% - 15px);
  box-sizing: border-box;
  border: 1px solid #e8eaec;
  padding: 20px;
  border-radius: 8px;
  background: #fff;
}

.group-title {
  margin-bottom: 20px;
}
</style>
